import { createContext, useContext, useEffect } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { auth, type AuthConfig, type AuthUser } from '@/lib/api'
import { ApiError } from '@/lib/errors'

export const authKeys = {
  me: ['auth', 'me'] as const,
  config: ['auth', 'config'] as const,
}

export interface AuthContextValue {
  user: AuthUser | null | undefined
  isAuthenticated: boolean
  isLoading: boolean
  query: UseQueryResult<AuthUser | null, ApiError>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

export function useUser(): AuthUser | null {
  const { user } = useAuth()
  return user ?? null
}

/** Same-origin relative path from `?next=`, else /dashboard. */
export function safeNextPath(search: string): string {
  const next = new URLSearchParams(search).get('next')
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/dashboard'
  return next
}

export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const safeNext = safeNextPath(location.search)

  return useMutation<void, ApiError, { email: string; password: string }>({
    mutationFn: async ({ email, password }) => {
      await auth.login(email, password)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: authKeys.me })
      const me = queryClient.getQueryData<AuthUser | null>(authKeys.me)
      if (me?.must_change_password) {
        navigate('/account/password', { replace: true })
        return
      }
      navigate(safeNext, { replace: true })
    },
  })
}

export function useChangePassword() {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, { current: string; next: string }>({
    mutationFn: async ({ current, next }) => {
      await auth.changePassword(current, next)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authKeys.me }),
  })
}

export function useLogout() {
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      try {
        await auth.logout()
      } catch {
        // best-effort
      }
    },
    onSettled: () => {
      window.location.assign('/login')
    },
  })
}

export function useAuthState(): AuthContextValue {
  const queryClient = useQueryClient()

  const query = useQuery<AuthUser | null, ApiError>({
    queryKey: authKeys.me,
    queryFn: async () => {
      try {
        const { data } = await auth.me()
        return data
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false
      return failureCount < 2
    },
    staleTime: 5 * 60 * 1_000,
    refetchOnWindowFocus: true,
    // The Microsoft SSO callback (backend `/auth/sso/callback`) sets the
    // session cookie and does a full-navigation 302 to the SPA root, which in
    // practice always tears down and rebuilds this hook's component tree, so
    // the query already runs fresh. `'always'` makes that a guarantee rather
    // than an accident of the redirect being a full page load: this hook (and
    // AuthProvider mounting it) must never serve a stale cached session on
    // mount, whether that's this SSO redirect or a future flow (e.g. a
    // same-tab remount) that doesn't happen to blow away the query cache.
    refetchOnMount: 'always',
  })

  useEffect(() => {
    const handler = () => queryClient.setQueryData(authKeys.me, null)
    window.addEventListener('auth:unauthorized', handler)
    return () => window.removeEventListener('auth:unauthorized', handler)
  }, [queryClient])

  const user = query.data ?? null

  return {
    user,
    isAuthenticated: !!user,
    isLoading: query.isLoading,
    query,
  }
}

// Unauthenticated config the login page polls to decide whether to render
// "Sign in with Microsoft". `staleTime: Infinity` because this is a
// per-deployment build-time setting, not something that changes mid-session —
// no point refetching it while the user sits on the login page.
export function useAuthConfig(): UseQueryResult<AuthConfig, ApiError> {
  return useQuery<AuthConfig, ApiError>({
    queryKey: authKeys.config,
    queryFn: async () => {
      const { data } = await auth.config()
      return data
    },
    staleTime: Infinity,
    retry: 1,
  })
}
